import { memo } from 'react';

import { Button } from '@picsart/design-system/components/Button';
import { IconGenAI } from '@picsart/design-system/foundation/icons/IconGenAI';
import { IconCoins } from '@picsart/design-system/foundation/icons/IconCoins';
import { IconBookmarkFilled } from '@picsart/design-system/foundation/icons/IconBookmarkFilled';
import { IconBookmarkOutline } from '@picsart/design-system/foundation/icons/IconBookmarkOutline';
import { IconEditOutline } from '@picsart/design-system/foundation/icons/IconEditOutline';
import { useTranslator } from '@picsart/rc/services/localization';

import useSavedTemplates from '@/hooks/useSavedTemplates';
import { isPortfolioApp as checkIsPortfolioApp } from '@/utils/environment/surface';
import { ScreenType } from '@/types/analytics';
import useStyles from './styles';

import type { FeedActionBarProps } from './types';

const FeedActionBar = ({
  label,
  onUpload,
  isLoading,
  slug,
  creditCount,
  shouldLoadCredits,
  canEdit,
  onEdit,
}: FeedActionBarProps) => {
  const classes = useStyles();
  const translate = useTranslator();
  const isPortfolioApp = checkIsPortfolioApp();
  const { isSaved, toggleSaved } = useSavedTemplates();
  const saved = isSaved(slug);
  const SaveIcon = saved ? IconBookmarkFilled : IconBookmarkOutline;

  return (
    <div className={classes.actionBar}>
      {/* One rail for every secondary action, so Edit stacks above Save without
          either needing an offset that encodes the other's height. Unlike Save,
          Edit is not gated on the portfolio surface — it ships on all touchpoints. */}
      <div className={classes.sideRail}>
        {canEdit && (
          <button
            type="button"
            className={classes.railButton}
            onClick={onEdit}
            aria-label="Edit inputs"
            data-testid="feed-edit"
          >
            <IconEditOutline className={classes.railIcon} fill="currentColor" />
            <span className={classes.railLabel}>{translate('Edit')}</span>
          </button>
        )}

        {isPortfolioApp && (
          <button
            type="button"
            className={classes.railButton}
            onClick={() => toggleSaved(slug, ScreenType.PRESET_FEED)}
            aria-label={saved ? 'Remove from saved' : 'Save'}
            data-testid="feed-save"
          >
            <SaveIcon className={classes.railIcon} fill="currentColor" />
            <span className={classes.railLabel}>{translate('Save')}</span>
          </button>
        )}
      </div>

      <Button
        dataTestId="preset-feed-upload-button"
        className={classes.uploadButton}
        variant="filled"
        size="large"
        isFullWidth
        startIcon={IconGenAI}
        onClick={onUpload}
        isLoading={isLoading}
      >
        {label}
      </Button>

      {shouldLoadCredits && !!creditCount && creditCount > 0 && (
        <div className={classes.creditsChip}>
          <IconCoins className={classes.creditsIcon} fill="currentColor" />
          <span>{creditCount}</span>
        </div>
      )}
    </div>
  );
};

export default memo(FeedActionBar);
